/**
 * MoneyByMonthChart — last 6 months of financial_transactions stacked by
 * status bucket (expected → pending → invoiced → paid). Colors = Finance page
 * status colors (validated palette). Refunds are shown in the tooltip only.
 *
 * Headline = REALIZED income only (paid + invoiced). Expected/pending are
 * money that may never arrive, so they are shown as their own figures (legend
 * + secondary line), never folded into "הכנסות".
 */

import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { Money } from "@phosphor-icons/react";
import { formatShekels } from "../../shared/nivDerived";
import { MONEY_SERIES, formatMonthKey, chartAnimationsEnabled } from "../clinicDetailConstants";
import useCountUp from "../../shared/hooks/useCountUp";
import { CardSkeleton } from "./Skeletons";

const MoneyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="ncd2-tooltip">
      <div className="ncd2-tooltip-label">{label}</div>
      {[...payload].reverse().map((entry) => (
        <div key={entry.dataKey} className="ncd2-tooltip-row">
          <span className="ncd2-tooltip-swatch" style={{ background: entry.fill || entry.color }} />
          <span>{entry.name}:</span>
          <strong>{formatShekels(entry.value)}</strong>
        </div>
      ))}
      <div className="ncd2-tooltip-row ncd2-tooltip-total">
        <span>סה"כ ({row.count || 0} תנועות):</span>
        <strong>{formatShekels(row.total || 0)}</strong>
      </div>
      {row.refundedCount > 0 && (
        <div className="ncd2-tooltip-row ncd2-muted">
          <span>החזרים: {row.refundedCount} ({formatShekels(row.refundedAmount || 0)})</span>
        </div>
      )}
    </div>
  );
};

const totalLabel = (v) => (v > 0 ? formatShekels(v) : "");

export default function MoneyByMonthChart({ moneyByMonth, isLoading }) {
  const rows = useMemo(
    () => (moneyByMonth || []).map((r) => ({ ...r, label: formatMonthKey(r.month) })),
    [moneyByMonth]
  );
  // Six-month sum per status — drives the headline and the legend amounts.
  const sums = useMemo(() => {
    const acc = { expected: 0, pending: 0, invoiced: 0, paid: 0, refundedAmount: 0 };
    rows.forEach((r) => {
      acc.expected += Number(r.expected) || 0;
      acc.pending += Number(r.pending) || 0;
      acc.invoiced += Number(r.invoiced) || 0;
      acc.paid += Number(r.paid) || 0;
      acc.refundedAmount += Number(r.refundedAmount) || 0;
    });
    return acc;
  }, [rows]);
  const realized = sums.paid + sums.invoiced;
  const shownRealized = useCountUp(Math.round(realized));
  const hasData = rows.some((r) => r.count > 0 || r.refundedCount > 0);

  if (isLoading) return <CardSkeleton height={180} lines={1} />;

  return (
    <section className="ncd2-card ncd2-money">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon">
          <Money size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">6 חודשים אחרונים · לפי סטטוס</span>
          <h3 className="ncd2-card-title">הכנסות הקליניקה בפועל</h3>
        </div>
        {hasData && (
          <div className="ncd2-money-sum">
            <strong title="שולם + חשבונית">{formatShekels(shownRealized)}</strong>
            <span className="ncd2-muted">
              ממתין {formatShekels(sums.pending)} · צפוי {formatShekels(sums.expected)}
            </span>
          </div>
        )}
      </header>

      {!hasData ? (
        <div className="ncd2-empty">אין תנועות כספיות ב־6 החודשים האחרונים</div>
      ) : (
        <>
          <div className="ncd2-money-chart" dir="ltr">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={rows} margin={{ top: 18, right: 4, left: 4, bottom: 0 }} barCategoryGap="28%">
                <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }} />
                <YAxis hide />
                <RechartsTooltip content={<MoneyTooltip />} cursor={{ fill: "rgba(77,217,180,0.08)" }} />
                {MONEY_SERIES.map((s, i) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    stackId="m"
                    fill={s.color}
                    stroke="var(--color-card)"
                    strokeWidth={2}
                    maxBarSize={34}
                    radius={i === MONEY_SERIES.length - 1 ? [4, 4, 0, 0] : 0}
                    isAnimationActive={chartAnimationsEnabled()}
                    animationDuration={600}
                  >
                    {i === MONEY_SERIES.length - 1 && (
                      <LabelList dataKey="total" position="top" formatter={totalLabel} fontSize={10} fill="var(--color-text-secondary)" />
                    )}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="ncd2-legend ncd2-money-legend" aria-label="מקרא — סכום לכל סטטוס">
            {MONEY_SERIES.map((s) => (
              <li key={s.key}>
                <span className="ncd2-legend-swatch" style={{ background: s.color }} />
                <span>{s.label}</span>
                <strong className="ncd2-money-legend-amount">{formatShekels(sums[s.key] || 0)}</strong>
              </li>
            ))}
            {sums.refundedAmount > 0 && (
              <li className="ncd2-muted">
                <span>החזרים</span>
                <strong className="ncd2-money-legend-amount">-{formatShekels(sums.refundedAmount)}</strong>
              </li>
            )}
          </ul>
        </>
      )}
    </section>
  );
}
